import { useSearchParams } from "react-router-dom";
import { useCallback, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

import { formatMilesDecimal } from "@/services/Utils";
import { getProductPriceUnitLabel } from "@/utils/unitFormatter";

import CustomButton from "@/components/desktop/button/CustomButton.jsx";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect.jsx";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm.jsx";
import SimpleTableCard from "@/components/desktop/list/simple-table/SimpleTableCard.jsx";

import useProductPriceHistoryController from "./hooks/useProductPriceHistoryController";
import useProductPriceHistoryColumns from "./hooks/useProductPriceHistoryColumns.jsx";

import styles from "./modal-product-price.module.scss";

const ModalProductPrice = () => {
	const [priceConfirmationState, setPriceConfirmationState] = useState({ open: false, price: null });
	const [recalculateConfirmOpen, setRecalculateConfirmOpen] = useState(false);
	const priceConfirmationResolver = useRef(null);

	const [searchParams] = useSearchParams();
	const productAction = searchParams.get("action");
	const productCode = searchParams.get("product");
	const modalIsOpen = Boolean(productAction === "price" && productCode);

	const {
		product,
		tableData,
		seasons,
		currentSeasonIndex,
		isLoadingAdditionalSeason,
		selectedEstateParentId,
		isLoadingSeasons,
		isLoadingPriceHistory,
		estateParentOptions,
		seasonOptions,
		handleEstateParentChange,
		handleSeasonChange,
		handleDocumentClick,
		handleClose,
		handlePriceSave,
		handleRecalculateFromOrigin,
		isRecalculatingPrice,
		activeEstateParentId,
		seasonAveragePrice,
	} = useProductPriceHistoryController();

	const requestPriceConfirmation = useCallback(
		({ newValue }) =>
			new Promise((resolve) => {
				priceConfirmationResolver.current = resolve;
				setPriceConfirmationState({ open: true, price: newValue });
			}),
		[],
	);

	const closePriceConfirmation = useCallback((nextValue) => {
		if (priceConfirmationResolver.current) {
			priceConfirmationResolver.current(nextValue);
			priceConfirmationResolver.current = null;
		}
		setPriceConfirmationState({ open: false, price: null });
	}, []);

	const handleConfirmPriceSave = useCallback(() => {
		closePriceConfirmation(true);
	}, [closePriceConfirmation]);

	const handleCancelPriceSave = useCallback(() => {
		closePriceConfirmation(false);
	}, [closePriceConfirmation]);

	const handleConfirmRecalculate = useCallback(() => {
		setRecalculateConfirmOpen(false);
		handleRecalculateFromOrigin();
	}, [handleRecalculateFromOrigin]);

	const selectedEstateParentIdOrDefault = selectedEstateParentId || estateParentOptions[0]?.value || "";
	const selectedEstateParentLabel = estateParentOptions.find((option) => option.value === selectedEstateParentIdOrDefault)?.label || "campo seleccionado";

	const priceHistoryColumns = useProductPriceHistoryColumns({
		product,
		handleDocumentClick,
		handlePriceSave,
		beforeSave: requestPriceConfirmation,
	});

	return (
		<CustomModalForm
			open={modalIsOpen}
			onClose={handleClose}
			title="Historial de Precios"
			size="md"
			closeOnBackdrop={false}
			closeOnEsc={true}
			showSave={false}
			isLoading={isLoadingSeasons}
			footerLeft={
				<CustomButton
					variant="primary_outline"
					size="sm"
					className={styles.recalculateButton}
					startIcon={<RefreshCw size={16} />}
					onClick={() => setRecalculateConfirmOpen(true)}
					disabled={!activeEstateParentId || isRecalculatingPrice || isLoadingSeasons || isLoadingPriceHistory}
					loading={isRecalculatingPrice}
				>
					Recalcular desde origen
				</CustomButton>
			}
		>
			<div className={styles.container}>
				{product && (
					<section className={styles.heroCard}>
						<div className={styles.heroBadge}>Precio del Producto</div>
						<h3 className={styles.heroTitle}>{product.name || "-"}</h3>
						<div className={styles.heroMetaGrid}>
							<div className={styles.heroMetaItem}>
								<span className={styles.heroMetaLabel}>
									Precio Promedio {seasons[currentSeasonIndex]?.label ? `— ${seasons[currentSeasonIndex].label}` : ""}
								</span>
								<strong className={styles.heroMetaValue}>
									${seasonAveragePrice != null ? formatMilesDecimal(seasonAveragePrice) : "0"} por{" "}
									{getProductPriceUnitLabel(product.unit_measurement)}
								</strong>
							</div>
						</div>
					</section>
				)}

				<SimpleTableCard
					columns={priceHistoryColumns}
					data={tableData}
					rowKey="id"
					loading={isLoadingSeasons || (isLoadingPriceHistory && tableData.length === 0)}
					loadingMessage="Cargando datos..."
					emptyMessage="Sin registros"
					rowClassName={(row) => (row.isEmptySeason ? styles.emptySeason : "")}
					extraLoading={isLoadingAdditionalSeason}
					extraLoadingMessage="Cargando temporada anterior..."
					toolbar={
						seasons.length > 0 ? (
							<div className={styles.seasonToolbar}>
								<div>
									{estateParentOptions.length > 0 && (
										<>
											<span className={styles.toolbarLabel}>Campo</span>
											<CustomInputSelect
												value={selectedEstateParentIdOrDefault}
												options={estateParentOptions}
												onChange={handleEstateParentChange}
												placeholder="Seleccionar campo"
												disabled={isLoadingPriceHistory || isLoadingAdditionalSeason}
												isClearable={false}
											/>
										</>
									)}
								</div>
								<div>
									<span className={styles.toolbarLabel}>Temporada</span>
									<CustomInputSelect
										value={currentSeasonIndex}
										options={seasonOptions}
										onChange={handleSeasonChange}
										placeholder="Seleccionar temporada"
										disabled={isLoadingPriceHistory || isLoadingAdditionalSeason}
										isClearable={false}
									/>
								</div>
							</div>
						) : undefined
					}
				/>
			</div>
			<CustomConfirmModal
				open={priceConfirmationState.open}
				onClose={handleCancelPriceSave}
				onConfirm={handleConfirmPriceSave}
				title="Confirmar precio manual"
				message={`Vas a guardar el precio manual de ${product?.name || "este producto"} en $${formatMilesDecimal(Number(priceConfirmationState.price || 0))} por ${getProductPriceUnitLabel(product?.unit_measurement)}.`}
				confirmText="Sí, guardar"
				cancelText="Cancelar"
			/>
			<CustomConfirmModal
				open={recalculateConfirmOpen}
				onClose={() => setRecalculateConfirmOpen(false)}
				onConfirm={handleConfirmRecalculate}
				title="Recalcular precios desde origen"
				message={`Se borrará el historial y el precio actual de ${product?.name || "este producto"} en ${selectedEstateParentLabel} y se reconstruirá desde todas las compras registradas en inventario, temporada por temporada. Los precios manuales quedarán reemplazados por el cálculo automático.`}
				confirmText="Sí, recalcular"
				cancelText="Cancelar"
				isLoading={isRecalculatingPrice}
			/>
		</CustomModalForm>
	);
};

export default ModalProductPrice;
